import { Dispatch, SetStateAction } from 'react';
import { getUnixTime } from 'date-fns';

import * as auth from '#/api/auth';
import { postAuth } from '#/api/account';
import { ParadexWebSocket } from '#/api/ws/ws';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { AuthState } from '#/features/auth/auth-context';
import { AuthRequest, buildAuthTypedData } from '#/features/wallet/chain-l2';
import StarknetWallet, {
  StarknetSignature,
  TypedData,
} from '#/features/starknet-wallet/wallet';
import { StarknetChainConfigurable } from '#/features/wallet/wallet-context';

import { AsyncResult } from '#/utils/types';
import { UnauthorizedError } from '#/utils/errors';

export default function actionPerformAuth(
  ...params: Parameters<typeof performAuth>
) {
  return async () => performAuth(...params);
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

async function performAuth(
  authState: AuthState,
  setAuthState: Dispatch<SetStateAction<AuthState>>,
  ws: ParadexWebSocket | undefined,
  options: StarknetChainConfigurable,
): AsyncResult<void> {
  if (ws == null) {
    throw new Error('auth called before WS available');
  }

  const actions = prepareActions(setAuthState);
  const isAuthSignedRequestAvailable = authState.signedRequest != null;

  actions.resetError();

  try {
    const authRequest = isAuthSignedRequestAvailable
      ? authState.signedRequest.request
      : prepareAuthRequest();
    const authRequestTypedData: TypedData = buildAuthTypedData(
      authRequest,
      options.starknetChainId,
    );
    const signature = isAuthSignedRequestAvailable
      ? authState.signedRequest.signature
      : StarknetWallet.signTypedData(authRequestTypedData);

    const httpAuthResp = await postAuth({
      signature,
      starknetAddress: StarknetWallet.getAddress(),
      createdAt: authRequest.timestamp,
      expiresAt: authRequest.expiration,
    });

    if (!httpAuthResp.ok) {
      if (httpAuthResp.status === 401) {
        const error = new UnauthorizedError(
          'Request to acquire JWT not authorized',
          { cause: httpAuthResp.error },
        );
        return {
          ok: false,
          reason: 'Not authorized to acquire authentication token',
          error,
        };
      }

      const error = new Error(`Request to acquire JWT failed`, {
        cause: httpAuthResp.error,
      });
      return {
        ok: false,
        reason: 'Failed to acquire authentication token',
        error,
      };
    }

    const wsAuthResp = await ws.request({
      method: 'auth',
      params: { bearer: httpAuthResp.data.jwt_token },
      timeout: 10_000,
    });

    if (wsAuthResp.error != null) {
      const error = new Error(`Error sending WS API auth request`, {
        cause: wsAuthResp.error,
      });
      return {
        ok: false,
        reason: 'Failed to authenticate real-time connection',
        error,
      };
    }

    actions.setAuthToken(httpAuthResp.data.jwt_token);
    actions.setAuthSignedRequest(authRequest, signature);
  } catch (err) {
    const description = 'Failed to authenticate user';
    const { message, isException } = prepareErrorMessage(description, err);
    actions.setError(message);
    const error = isException ? new Error(description, { cause: err }) : null;
    return { ok: false, reason: message, error };
  }

  return { ok: true, data: undefined };
}

function prepareActions(setState: Dispatch<SetStateAction<AuthState>>) {
  const setAuthToken = (token: string) => {
    auth.setAuthToken(token);
    setState((state) => ({ ...state, token }));
  };

  const setAuthSignedRequest = (
    request: AuthRequest,
    signature: StarknetSignature,
  ) => {
    setState((state) => ({ ...state, signedRequest: { request, signature } }));
  };

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: null }));
  };

  return {
    setAuthToken,
    setAuthSignedRequest,
    setError,
    resetError,
  };
}

function prepareAuthRequest(): AuthRequest {
  const dateNow = new Date();
  const dateExpiration = new Date(dateNow.getTime() + SEVEN_DAYS_MS);

  return {
    method: 'POST',
    path: '/v1/auth',
    body: '',
    timestamp: getUnixTime(dateNow),
    expiration: getUnixTime(dateExpiration),
  };
}
